import { useState } from "react";
import { useLocation } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { useLotes } from "@/hooks/use-data";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Boxes, Building2, Calendar, ChevronRight, FileText, Lock, Unlock, User, Loader2, RefreshCw } from "lucide-react";
import { MONTH_NAMES } from "@/lib/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { api } from "@shared/routes";

function formatBRL(value: number | null | undefined) {
  if (value == null) return null;
  return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function Lotes() {
  const { profile } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isOwner = profile?.roles?.includes("owner") ?? false;
  const isAdmin = profile?.roles?.includes("admin") ?? false;
  const empresaId = isOwner ? undefined : profile?.empresa?.id;
  const { data: lotes, isLoading } = useLotes(empresaId);
  const [fechandoId, setFechandoId] = useState<string | null>(null);

  const fecharLote = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/lotes/${id}/fechar`).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.lotes.list.path] });
      toast({ title: "Lote fechado com sucesso!" });
    },
    onError: () => toast({ title: "Erro ao fechar lote", variant: "destructive" }),
    onSettled: () => setFechandoId(null),
  });

  const sincronizarContagem = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/admin/sync-lotes").then((r) => r.json()),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [api.lotes.list.path] });
      toast({
        title: "Sincronização concluída",
        description: `${data.criados ?? 0} lote(s) criado(s), ${data.atualizados ?? 0} atualizado(s).`,
      });
    },
    onError: () =>
      toast({ title: "Erro ao sincronizar lotes", variant: "destructive" }),
  });

  return (
    <Shell>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-3xl font-bold font-display text-slate-900"
            data-testid="text-page-title"
          >
            Lotes Mensais
          </h1>
          <p className="text-muted-foreground mt-1">
            Agrupamento mensal de laudos finalizados
          </p>
        </div>
        {isOwner && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={sincronizarContagem.isPending}
            onClick={() => sincronizarContagem.mutate()}
          >
            {sincronizarContagem.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Sincronizar Contagem
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {isLoading ? (
          Array(4)
            .fill(0)
            .map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
        ) : lotes && lotes.length > 0 ? (
          lotes.map((lote) => (
            <Card
              key={lote.id}
              className="border-none shadow-md hover:shadow-lg transition-shadow"
              data-testid={`card-lote-${lote.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${lote.fechado ? "bg-green-100" : "bg-amber-100"}`}
                  >
                    {lote.fechado ? (
                      <Lock className="w-6 h-6 text-green-600" />
                    ) : (
                      <Unlock className="w-6 h-6 text-amber-600" />
                    )}
                  </div>
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => navigate(`/lotes/${lote.id}`)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900">
                        {MONTH_NAMES[lote.mes - 1]} {lote.ano}
                      </h3>
                      <Badge
                        variant="secondary"
                        className={
                          lote.fechado
                            ? "bg-green-100 text-green-700"
                            : "bg-amber-100 text-amber-700"
                        }
                      >
                        {lote.fechado ? "Fechado" : "Aberto"}
                      </Badge>
                      {(lote as any).parceiroNome && (
                        <Badge variant="outline" className="gap-1 text-slate-600">
                          <User className="w-3 h-3" />
                          {(lote as any).parceiroNome}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      {isOwner && lote.vistoriadoraNome && (
                        <div className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {lote.vistoriadoraNome}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" />
                        {lote.qtdLaudos} laudo(s)
                      </div>
                      {lote.fechadoEm && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Fechado em:{" "}
                          {new Date(lote.fechadoEm).toLocaleDateString("pt-BR")}
                        </div>
                      )}
                    </div>
                    {formatBRL(lote.valorEstimado) && (
                      <div className="mt-2 flex items-center gap-1.5">
                        <span className="text-xs text-slate-400">Total estimado</span>
                        <span className={`text-sm font-semibold ${lote.fechado ? "text-green-700" : "text-blue-700"}`}>
                          {formatBRL(lote.valorEstimado)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {(isAdmin || isOwner) && !lote.fechado && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                        disabled={fechandoId === lote.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFechandoId(lote.id);
                          fecharLote.mutate(lote.id);
                        }}
                      >
                        {fechandoId === lote.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Lock className="w-3.5 h-3.5" />
                        )}
                        Fechar
                      </Button>
                    )}
                    {(isAdmin || isOwner) && lote.fechado && (lote as any).parceiroNome && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-blue-700 border-blue-300 hover:bg-blue-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/faturas-parceiro?lote_id=${lote.id}`);
                        }}
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Criar Fatura
                      </Button>
                    )}
                    <ChevronRight
                      className="w-5 h-5 text-slate-300 cursor-pointer"
                      onClick={() => navigate(`/lotes/${lote.id}`)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-16 text-muted-foreground">
            <Boxes className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            Nenhum lote encontrado
          </div>
        )}
      </div>
    </Shell>
  );
}
